import type { ComputedRef, InjectionKey, Ref } from "vue";
import type {
  Correlations,
  DeviationSurvey,
  Equipment,
  FormItems,
  OperatingParameters,
  PerfEval,
  Scenario,
  Sizing,
  WellProperties,
  XrefCables,
  XrefHousings,
  XrefIntakes,
  XrefPumps,
  XrefSeals
} from "@/composables/forms/formTypes";
import type {
  XrefYToolBypassTubings,
  XrefYToolEquipmentTubings,
  DestructuredScenario
} from "@/composables/useDestructureScenario";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { UseScenarioValidator } from "@/composables/validation/scenarioValidator";

// Provided in SizingDetail
export const sizingKey: InjectionKey<Ref<Sizing | undefined>> =
  Symbol("sizing");
export const sizingFormKey: InjectionKey<FormItems["sizing"]> =
  Symbol("sizingForm");

export const CalculatorDic: InjectionKey<
  Ref<Record<string, HTMLElement | null>>
> = Symbol("calDomEle");

export const scenariosKey: InjectionKey<Ref<Scenario[]>> = Symbol("scenarios");

// Provided in scenarioDetailProvider
export const scenarioKey: InjectionKey<Ref<Scenario | null | undefined>> =
  Symbol("scenario");
export const scenarioFormKey: InjectionKey<FormItems["scenario"]> =
  Symbol("scenarioForm");

export const scenarioCalculationsKey: InjectionKey<ScenarioCalculations> =
  Symbol("scenarioCalculations");
export const stackCalculationsKey: InjectionKey<
  (stack: "upper" | "lower") => ScenarioCalculations
> = Symbol("stackCalculations");
export const upperCalculationsKey: InjectionKey<
  ComputedRef<ScenarioCalculations>
> = Symbol("upperCalculations");
export const lowerCalculationsKey: InjectionKey<
  ComputedRef<ScenarioCalculations>
> = Symbol("lowerCalculations");

export const equipmentKey: InjectionKey<Ref<Equipment>> = Symbol("equipment");
export const equipmentFormKey: InjectionKey<FormItems["equipment"]> =
  Symbol("equipmentForm");

export const correlationsKey: InjectionKey<Ref<Correlations>> =
  Symbol("correlations");
export const correlationsFormKey: InjectionKey<FormItems["correlations"]> =
  Symbol("correlationsForm");

export const parametersKey: InjectionKey<Ref<OperatingParameters>> =
  Symbol("parameters");
export const parametersFormKey: InjectionKey<FormItems["parameters"]> =
  Symbol("parametersForm");

export const wellPropertiesKey: InjectionKey<Ref<WellProperties>> =
  Symbol("wellProperties");
export const wellPropertiesFormKey: InjectionKey<FormItems["wellProperties"]> =
  Symbol("wellPropertiesForm");

export const deviationSurveyKey: InjectionKey<Ref<DeviationSurvey>> =
  Symbol("deviationSurvey");
export const deviationSurveyFormKey: InjectionKey<
  FormItems["deviationSurvey"]
> = Symbol("deviationSurveyForm");

export const sealsMapKey: InjectionKey<Ref<Record<string, XrefSeals[number]>>> =
  Symbol("seals");
export const sealsFormKey: InjectionKey<FormItems["seals"]> =
  Symbol("sealsForm");

export const intakesMapKey: InjectionKey<
  Ref<Record<string, XrefIntakes[number]>>
> = Symbol("intakes");
export const intakesFormKey: InjectionKey<FormItems["intakes"]> =
  Symbol("intakesForm");

export const cablesMapKey: InjectionKey<
  Ref<Record<string, XrefCables[number]>>
> = Symbol("cables");
export const cablesFormKey: InjectionKey<FormItems["cables"]> =
  Symbol("cablesForm");

export const pumpsMapKey: InjectionKey<Ref<Record<string, XrefPumps[number]>>> =
  Symbol("pumps");
export const pumpsFormKey: InjectionKey<FormItems["pumps"]> =
  Symbol("pumpsForm");

export const housingsMapKey: InjectionKey<
  Ref<Record<string, XrefHousings[number]>>
> = Symbol("housings");
export const housingsFormKey: InjectionKey<FormItems["housings"]> =
  Symbol("housingsForm");

export const yToolBypassTubingsMapKey: InjectionKey<
  Ref<Record<string, XrefYToolBypassTubings[number]>>
> = Symbol("yToolBypassTubings");
export const yToolBypassTubingsFormKey: InjectionKey<
  FormItems["yToolBypassTubings"]
> = Symbol("yToolBypassTubingsForm");

export const yToolEquipmentTubingsMapKey: InjectionKey<
  Ref<Record<string, XrefYToolEquipmentTubings[number]>>
> = Symbol("yToolEquipmentTubings");
export const yToolEquipmentTubingsFormKey: InjectionKey<
  FormItems["yToolEquipmentTubings"]
> = Symbol("yToolEquipmentTubingsForm");

export const scenarioValidatorKey: InjectionKey<UseScenarioValidator> =
  Symbol("scenarioValidator");

export const upperFormsKey: InjectionKey<ComputedRef<DestructuredScenario>> =
  Symbol("upperForms");
export const lowerFormsKey: InjectionKey<ComputedRef<DestructuredScenario>> =
  Symbol("lowerForms");

// Provided inside setup of ScenarioDetail
export const perfevalsKey: InjectionKey<Ref<PerfEval[]>> = Symbol("perfevals");
export const isDirtyKey: InjectionKey<() => boolean> = Symbol("isDirty");

// Provided inside PerformanceEvaluation
export const perfevalFormKey: InjectionKey<FormItems["performanceEvaluation"]> =
  Symbol("performanceEvaluation");
export const perfevalPumpsFormKey: InjectionKey<FormItems["perfEvalPumps"]> =
  Symbol("perfevalXrefPumps");
export const perfevalPumpsMapKey: InjectionKey<
  ComputedRef<Record<string, XrefPumps[number]>>
> = Symbol("perfevalPumps");
